import { useCallback, useMemo } from "react"

import type { AnswerWithDiscussion } from "../components/QuestionnaireReview/types"
import { useSearchParamValues } from "./useSearchParamValues"

export interface FeedFilterState {
  lastReviewedBy: string[]
  confidence: string | null
  lastAssignedTo: string[]
  approved: string | null
  discussionAssignedTo: string[]
  oid: string[]
  section: string[]
}

export interface UseFeedFiltersResult {
  filterState: FeedFilterState
  setFilterValue: (
    key: keyof FeedFilterState,
    value: string[] | string | null | undefined,
  ) => void
  resetFilters: () => void
  filterAnswers: (answers: AnswerWithDiscussion[]) => AnswerWithDiscussion[]
  activeFilterCount: number
}

function parseUrlParamArray(value: string | null): string[] {
  return value ? value.split(",").filter(Boolean) : []
}

export function useFeedFilters(): UseFeedFiltersResult {
  const { searchParams, setValue, reset } = useSearchParamValues<
    keyof FeedFilterState
  >(/*useCommaSepArrays=*/ true)

  const filterState = useMemo<FeedFilterState>(
    () => ({
      lastReviewedBy: parseUrlParamArray(searchParams.get("lastReviewedBy")),
      confidence: searchParams.get("confidence"),
      lastAssignedTo: parseUrlParamArray(searchParams.get("lastAssignedTo")),
      oid: parseUrlParamArray(searchParams.get("oid")),
      approved: searchParams.get("approved"),
      discussionAssignedTo: parseUrlParamArray(
        searchParams.get("discussionAssignedTo"),
      ),
      section: parseUrlParamArray(searchParams.get("section")),
    }),
    [searchParams],
  )

  const filterAnswers = useCallback(
    (answers: AnswerWithDiscussion[]) => {
      return answers.filter((answer) => {
        let isIncluded = true

        if (filterState.oid.length > 0) {
          isIncluded &&= filterState.oid.includes(answer.oid)
        }

        if (filterState.lastReviewedBy.length > 0) {
          isIncluded &&= filterState.lastReviewedBy.includes(
            answer.last_reviewed_by?.uid ?? "",
          )
        }

        if (filterState.confidence === "0") {
          isIncluded &&= answer.confidence === 0
        } else if (filterState.confidence === "1") {
          isIncluded &&= answer.confidence > 0
        }

        if (filterState.lastAssignedTo.length > 0) {
          isIncluded &&= filterState.lastAssignedTo.includes(
            answer.last_assigned_to?.uid || "",
          )
        }

        if (filterState.approved) {
          const approved = !!answer.last_reviewed_by
          if (filterState.approved === "1") {
            isIncluded &&= approved
          } else if (filterState.approved === "0") {
            isIncluded &&= !approved
          }
        }

        if (filterState.discussionAssignedTo.length > 0) {
          isIncluded &&=
            answer.discussions?.some((discussion) =>
              filterState.discussionAssignedTo.includes(
                discussion.assignment?.uid ?? "",
              ),
            ) ?? false
        }

        if (filterState.section.length > 0) {
          isIncluded &&= filterState.section.includes(
            answer.primary_question.location?.sheetName ?? "",
          )
        }

        return isIncluded
      })
    },
    [filterState],
  )

  const activeFilterCount = Object.values(filterState).filter((value) =>
    Array.isArray(value) ? value.length > 0 : value !== null,
  ).length

  return {
    filterState,
    setFilterValue: setValue,
    resetFilters: reset,
    filterAnswers,
    activeFilterCount,
  }
}
